import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PWAInstallPrompt from '../components/features/PWAInstallPrompt';
import { fetchShopDeliveryInfo } from '../utils/shopFreeShipping';

const Policies = () => {
    const [shopPolicy, setShopPolicy] = useState({ status: 'loading' });

    useEffect(() => {
        const run = async () => {
            const businessId = (process.env.REACT_APP_BUSINESS_ID || '').trim();
            if (!businessId) {
                setShopPolicy({ status: 'noBusiness' });
                return;
            }
            const isProduction =
                typeof window !== 'undefined' &&
                (window.location.hostname.includes('rainbow-sails.shop') ||
                    (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
            const apiBaseV1 =
                process.env.REACT_APP_API_URL ||
                (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
            const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId });
            setShopPolicy({
                status: 'ready',
                showFreeShippingPromo: d.showFreeShippingPromo,
                freeThreshold: d.freeThreshold,
                customerPickupEnabled: d.customerPickupEnabled,
            });
        };
        void run();
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            position: 'relative',
            background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
            color: 'white',
            overflow: 'hidden'
        }}>
            <div style={{
                position: 'relative',
                zIndex: 10,
                padding: window.innerWidth <= 768 ? '20px 16px' : '20px',
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
            }}>
                <div style={{ 
                  maxWidth: '1280px', 
                  margin: '0 auto',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                    {/* Featured Image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1, delay: 0.4 }}
                        style={{
                            textAlign: 'center',
                            marginBottom: '8px',
                            paddingTop: '20px'
                        }}
                    >
                        <img
                            src="/favicon.png"
                            alt="Rainbow Sails"
                            style={{
                                width: '25%',
                                maxWidth: '200px',
                                height: 'auto',
                                margin: '0 auto'
                            }}
                        />
                    </motion.div>

                    {/* Header */}
                    <header style={{ textAlign: 'center', marginBottom: '64px', paddingTop: '16px' }}>
                        <motion.h1 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8 }}
                            style={{
                                fontSize: 'clamp(3rem, 8vw, 5rem)',
                                fontWeight: 'bold',
                                marginBottom: '24px',
                                color: '#10b981'
                            }}
                        >
                            Policies & Trust
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            style={{
                                fontSize: 'clamp(1.25rem, 3vw, 1.875rem)',
                                color: '#22d3ee',
                                marginBottom: '16px'
                            }}
                        >
                            📋 Everything You Need to Know • 🚚 Shipping & Returns • 🔒 Privacy & Security
                        </motion.p>
                    </header>

                    {/* Shipping Policy Section */}
                    <section id="shipping" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(236, 72, 153, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #10b981'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#10b981' }}>🚚 Shipping Policy</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Delivery &amp; shipping</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We ship across Australia and to international addresses. Your shipping cost is worked out at checkout from your items and your delivery address — you’ll see the full amount before you pay.
                                    </p>
                                    {shopPolicy.status === 'ready' && shopPolicy.showFreeShippingPromo && (
                                        <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6', marginTop: '12px' }}>
                                            This store’s published free-delivery line is: <strong>free standard delivery</strong> when your subtotal is over{' '}
                                            <strong>${Number(shopPolicy.freeThreshold).toFixed(2)} AUD</strong> (including GST). <strong>Checkout</strong> is where your shipping line and total are final.
                                        </p>
                                    )}
                                    {shopPolicy.status === 'ready' && !shopPolicy.showFreeShippingPromo && (
                                        <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6', marginTop: '12px' }}>
                                            This store does <strong>not</strong> list a public “free over $X” amount here. <strong>Your shipping cost</strong> is calculated at checkout from your order and your delivery address, and is shown in full before you pay.
                                        </p>
                                    )}
                                    {(shopPolicy.status === 'loading' || shopPolicy.status === 'noBusiness') && (
                                        <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6', marginTop: '12px' }}>
                                            For this site, the exact shipping line appears at checkout.
                                        </p>
                                    )}
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6', marginTop: '12px' }}>
                                        Some products are made to order and despatched by our print partners; others ship from our own stock. Delivery time, tracking, and any customs or import costs depend on your country — you’ll see the details in your order confirmation.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>
                                        {shopPolicy.status === 'ready' && shopPolicy.customerPickupEnabled
                                            ? 'Local pickup'
                                            : 'Receiving your order'}
                                    </h3>
                                    {shopPolicy.status === 'ready' && shopPolicy.customerPickupEnabled && (
                                        <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                            This store offers <strong>in-person collection</strong> for eligible self-fulfilled items when the contents of your cart allow it. Print-on-demand and partner-shipped lines are still delivered to you. You’ll see whether pickup applies and any address or hours at checkout.
                                        </p>
                                    )}
                                    {shopPolicy.status === 'ready' && !shopPolicy.customerPickupEnabled && (
                                        <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                            This store <strong>ships to your address</strong> only. <strong>Local pickup is not available.</strong>
                                        </p>
                                    )}
                                    {(shopPolicy.status === 'loading' || shopPolicy.status === 'noBusiness') && (
                                        <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                            Delivery and pickup options (if the store has enabled them) are shown at checkout.
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Processing time</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Most orders move into production or packing within about 2–5 business days so custom and on-demand items can be made and checked. You will get a shipping confirmation with tracking when the carrier or partner hands off the parcel (timing can vary by product and destination).
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#4ade80' }}>Delivery times (after fulfillment)</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        <strong>Global:</strong> After your order leaves the warehouse or print partner, delivery speed depends on your country, distance, and the service selected. Many routes complete within a few business days; longer cross-border or remote destinations often fall in a roughly <strong>5–20 business day</strong> range, and can sometimes take longer.<br/>
                                        <br/>
                                        <strong>Customs and imports:</strong> International orders may be subject to duties, taxes, or customs processing in your country. Those fees and delays are outside our control and are your responsibility where applicable.<br/>
                                        <br/>
                                        <em>Exact options and estimates appear at checkout for your address.</em>
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Return & Refund Policy Section */}
                    <section id="returns" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(34, 211, 238, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #22d3ee'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#22d3ee' }}>🔄 Return & Refund Policy</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Printful's No-Return Policy</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Due to Printful's manufacturing process and quality standards, we cannot accept returns on most items. 
                                        This policy helps us keep prices affordable while ensuring you receive high-quality products.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Quality Guarantee</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We only work with Printful because of their exceptional quality standards. Every item is 
                                        printed on-demand with premium materials and undergoes quality control before shipping.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Damaged or Defective Items</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        If you receive a damaged or defective item, contact us immediately with photos. We'll work with 
                                        Printful to get you a replacement at no cost to you.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#7dd3fc' }}>Why This Policy?</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Printful creates each item specifically for you when you order. This on-demand approach 
                                        reduces waste and keeps costs down, but means we can't resell returned items.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Privacy Policy Section */}
                    <section id="privacy" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.5), rgba(139, 92, 246, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #f472b6'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#f472b6' }}>🔒 Privacy Policy</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>Information We Collect</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We only collect information necessary to process your order and provide customer service: 
                                        name, email, shipping address, and payment information.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>How We Use Your Information</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Your information is used solely to process orders, communicate about your purchase, 
                                        and provide customer support. We never sell or share your personal information.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>Payment Security</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        All payments are processed securely through Stripe, a trusted payment processor. 
                                        We never store your credit card information on our servers.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#fbcfe8' }}>Your Rights</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        You have the right to access, update, or delete your personal information at any time. 
                                        Contact us if you need to exercise these rights.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Terms of Service Section */}
                    <section id="terms" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.0 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(34, 211, 238, 0.5), rgba(59, 130, 246, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #3b82f6'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#3b82f6' }}>📜 Terms of Service</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Order Acceptance</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        All orders are subject to acceptance and availability. We reserve the right to refuse service 
                                        to anyone for any reason at any time.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Intellectual Property</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        All artwork, designs, and content on this site are the intellectual property of Zee Sails. 
                                        Reproduction or distribution without permission is prohibited.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Limitation of Liability</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Rainbow Sails is not liable for any indirect, incidental, or consequential damages arising 
                                        from your use of our products or services.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#93c5fd' }}>Governing Law</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        These terms are governed by the laws of Australia. Any disputes will be resolved 
                                        through good faith communication and negotiation.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Customer Service Section */}
                    <section id="customer-service" style={{ marginBottom: '64px' }}>
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.2 }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.5), rgba(5, 150, 105, 0.5))',
                                backdropFilter: 'blur(4px)',
                                borderRadius: '12px',
                                padding: '32px',
                                border: '2px solid #10b981'
                            }}
                        >
                            <h2 style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '24px', color: '#10b981' }}>💬 Customer Service</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#6ee7b7' }}>We're Here to Help</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Have questions about our policies or need help with your order? Our customer service team 
                                        is here to help you every step of the way.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#6ee7b7' }}>Contact Methods</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        Primary support: sign in on the shop and open My Account → Support (call booking and tools).<br/>
                                        Email: support@rainbow-sails.shop<br/>
                                        Response Time: Within 24 hours during business days
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '12px', color: '#6ee7b7' }}>Our Commitment</h3>
                                    <p style={{ fontSize: '18px', color: '#d1d5db', lineHeight: '1.6' }}>
                                        We're committed to providing exceptional customer service and ensuring you have a 
                                        positive experience with every purchase.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </section>

                    {/* Call to Action Section */}
                    <section style={{
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(139, 92, 246, 0.2))',
                        borderRadius: '12px',
                        padding: '32px',
                        border: '2px solid #a78bfa'
                    }}>
                        <motion.h2 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.6 }}
                            style={{
                                fontSize: '2.25rem',
                                fontWeight: 'bold',
                                marginBottom: '16px',
                                color: '#a78bfa'
                            }}
                        >
                            Ready to Shop with Confidence? 🛒
                        </motion.h2>
                        <motion.p 
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.7 }}
                            style={{
                                fontSize: '20px',
                                marginBottom: '24px',
                                maxWidth: '48rem',
                                margin: '0 auto 24px'
                            }}
                        >
                            We've got everything covered - from secure payments to quality guarantees. 
                            Shop with confidence knowing your satisfaction and security are our top priorities.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.8 }}
                        >
                            <Link
                                to="/gallery"
                                style={{
                                    display: 'inline-block',
                                    background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                                    color: 'white',
                                    padding: '16px 32px',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    fontSize: '20px',
                                    textDecoration: 'none',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #db2777, #7c3aed)';
                                    e.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'linear-gradient(135deg, #ec4899, #8b5cf6)';
                                    e.target.style.transform = 'scale(1)';
                                }}
                            >
                                🚀 Explore the Gallery
                            </Link>
                        </motion.div>
                        <p style={{ fontSize: '14px', color: '#9ca3af', marginTop: '16px' }}>
                            Secure shopping • Worldwide shipping calculated at checkout • Quality guaranteed • 24/7 support
                        </p>
                    </section>
                </div>
            </div>
            <PWAInstallPrompt />
        </div>
    );
};

export default Policies;